<template>
  <div class="editor-wrapper" @click.stop @mousedown.stop>
    <div class="toolbar" v-show="editor" @click.stop>
      <button @click="exec('undo')">↺</button>
      <button @click="exec('redo')">↻</button>
      <button class="bold-btn" @click="exec('toggleBold')">B</button>
      <button class="italic-btn" @click="exec('toggleItalic')">I</button>
      <button class="underline-btn" @click="exec('toggleUnderline')">U</button>
      <!--<button @click="exec('toggleBulletList')">• List</button>-->

      <!-- HIGHLIGHT button -->
      <div class="highlight-btn" @click.stop="toggleHighlightPalette">
        <span class="highlight-icon" :style="{ color: highlightColor }"></span>
        <span class="highlight-color-bar" :style="{ backgroundColor: highlightColor }"></span>
      </div>

      <!-- TEXT COLOR button -->
      <div class="color-btn" @click.stop="toggleTextColorPalette" :style="{ borderBottomColor: fontColor }">
        A
      </div>

      <select v-model="fontSize" @change="e => exec('setFontSize', fontSize)">
        <option value="12px">12</option>
        <option value="14px">14</option>
        <option value="16px">16</option>
        <option value="18px">18</option>
        <option value="24px">24</option>
        <option value="32px">32</option>
      </select>
    </div>

    <!-- PALETTE TESTO -->
    <div v-if="showTextColorPalette" class="color-palette text-palette" @mousedown.stop>
      <div v-for="c in colors" :key="`text-` + c" class="color-box" :style="{ backgroundColor: c }"
        @click="chooseTextColor(c)"></div>

      <button class="remove-color" @click="clearTextColor">✖</button>
    </div>

    <!-- PALETTE HIGHLIGHT -->
    <div v-if="showHighlightPalette" class="color-palette highlight-palette" @mousedown.stop>
      <div v-for="c in colors" :key="`high-` + c" class="color-box" :style="{ backgroundColor: c }"
        @click="chooseHighlightColor(c)"></div>

      <button class="remove-color" @click="removeHighlight">✖</button>
    </div>

    <EditorContent :editor="editor" class="editor-content" @click.stop @mousedown.stop @mouseup.stop @keydown.stop
      @input.stop />
  </div>
</template>

<script>
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useEditor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Highlight from '@tiptap/extension-highlight'
import Color from '@tiptap/extension-color'
import { TextStyle } from '@tiptap/extension-text-style'
import { Extension } from '@tiptap/core'
import Underline from '@tiptap/extension-underline'

const CustomHighlight = Highlight.extend({
  addOptions() {
    return {
      multicolor: true,
      HTMLAttributes: {},
    }
  },
})

const FontSize = Extension.create({
  name: 'fontSize',
  addOptions() {
    return { types: ['textStyle'] }
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: element => element.style.fontSize.replace(/['"]+/g, ''),
            renderHTML: attributes => {
              if (!attributes.fontSize) return {}
              return { style: `font-size: ${attributes.fontSize}` }
            },
          },
        },
      },
    ]
  },
  addCommands() {
    return {
      setFontSize:
        fontSize =>
          ({ chain }) => {
            return chain().setMark('textStyle', { fontSize }).run()
          },
    }
  },
})

export default {
  props: ['modelValue', 'stateId', 'editable'],
  emits: ['update:modelValue'],
  components: { EditorContent },

  setup(props, { emit }) {
    const fontColor = ref('#000000')
    const highlightColor = ref('#fff59d')
    const fontSize = ref('16px')

    const showTextColorPalette = ref(false)
    const showHighlightPalette = ref(false)

    const colors = [
      '#000000', '#FF0000', '#008000', '#0000FF',
      '#FFA500', '#800080', '#00AAAA', '#FFFF00',
      '#C0C0C0', '#FFFFFF'
    ]

    function setFontColor(color) {
      fontColor.value = color
      exec('setColor', color)
    }

    function chooseTextColor(color) {
      setFontColor(color)
      showTextColorPalette.value = false
    }
    function clearTextColor() {
      if (!editor?.value) return
      editor.value.chain().focus().unsetColor ? editor.value.chain().focus().unsetColor().run() : editor.value.chain().focus().setColor(null).run()
      showTextColorPalette.value = false
      fontColor.value = '#000000'
    }

    function applyHighlight() {
      if (!editor?.value) return

      const { from, to, empty } = editor.value.state.selection

      if (empty) return

      editor.value.chain()
        .focus()
        .unsetHighlight()
        .setMark('highlight', { color: highlightColor.value })
        .run()
    }


    function chooseHighlightColor(color) {
      highlightColor.value = color
      exec('setHighlight')
      showHighlightPalette.value = false
    }

    function removeHighlight() {
      if (!editor?.value) return
      editor.value.chain().focus().unsetHighlight().run()
      showHighlightPalette.value = false
    }

    function toggleTextColorPalette() {
      showHighlightPalette.value = false
      showTextColorPalette.value = !showTextColorPalette.value
    }

    function toggleHighlightPalette() {
      showTextColorPalette.value = false
      showHighlightPalette.value = !showHighlightPalette.value
    }

    function onDocClick() {
      showTextColorPalette.value = false
      showHighlightPalette.value = false
    }

    const editor = useEditor({
      extensions: [
        StarterKit.configure({ history: false }),
        TextStyle.configure({ history: false }),
        Color.configure({ history: false }),
        FontSize,
        CustomHighlight,
        Underline,
      ],
      content: props.modelValue || '',
      autofocus: true,
      editorProps: { attributes: { tabindex: 0 } },
      onUpdate: ({ editor }) => emit('update:modelValue', editor.getHTML()),
      editable: props.editable,
    })

    function exec(command, value = null) {
      if (!editor?.value) return
      const map = {
        toggleBold: () => editor.value.chain().focus().toggleBold().run(),
        toggleItalic: () => editor.value.chain().focus().toggleItalic().run(),
        toggleStrike: () => editor.value.chain().focus().toggleStrike().run(),
        toggleBulletList: () => editor.value.chain().focus().toggleBulletList().run(),
        undo: () => editor.value.chain().focus().undo().run(),
        redo: () => editor.value.chain().focus().redo().run(),

        setColor: () => {
          const currentColor = editor.value.getAttributes('textStyle').color
          if (currentColor === value) {
            editor.value.chain().focus().unsetColor().run()
          } else {
            editor.value.chain().focus().setColor(value).run()
          }
        },

        setHighlight: () => {
          const { from, to, empty } = editor.value.state.selection
          if (empty) return
          editor.value.chain()
            .focus()
            .setMark('highlight', { color: highlightColor.value })
            .run()
        },
        setFontSize: () => editor.value.chain().focus().setMark('textStyle', { fontSize: value }).run(),

        toggleUnderline: () => editor.value.chain().focus().toggleUnderline().run(),
      }
      const fn = map[command]
      if (fn) fn()
    }

    onMounted(() => {
      document.addEventListener('click', onDocClick)
    })

    onBeforeUnmount(() => {
      document.removeEventListener('click', onDocClick)
      if (editor) {
        try {
          editor.destroy()
        } catch (e) { }
      }
    })


    watch(
      () => props.editable,
      (val) => {
        if (editor?.value) editor.value.setEditable(val)
      },
      { immediate: true }
    )

    /* return everything used in template (includes new palette refs & methods) */
    return {
      editor,
      exec,
      fontColor,
      setFontColor,
      highlightColor,
      applyHighlight,
      fontSize,
      /* ADDED */
      showTextColorPalette,
      showHighlightPalette,
      colors,
      toggleTextColorPalette,
      toggleHighlightPalette,
      chooseTextColor,
      chooseHighlightColor,
      removeHighlight,
      clearTextColor,
    }
  },
}
</script>

<style scoped>
.editor-wrapper {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-height: 200px;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 8px;
  background: #fff;
  position: relative;
}

.toolbar {
  display: flex;
  gap: 4px;
  margin-bottom: 8px;
  position: relative;
}

.toolbar button {
  border: none;
  background: none;
  cursor: pointer;
  padding: 4px 8px;
  font-weight: bold;
  font-size: 14px;
}

.toolbar input,
.toolbar select {
  margin-left: 4px;
}

.editor-content {
  flex-grow: 1;
  min-height: 150px;
  padding: 8px;
  border: 1px solid #eee;
  border-radius: 4px;
  background: white;
  overflow-y: auto;
  outline: none !important;
  box-shadow: none !important;
}

:deep(.ProseMirror) {
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
}

.bold-btn {
  font-weight: bold;
}

.italic-btn {
  font-style: italic;
}

.underline-btn {
  text-decoration: underline;
}

.color-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-weight: bold;
  font-size: 16px;
  padding: 2px 6px;
  border-bottom: 3px solid #000;
}

.highlight-btn {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  margin-right: 4px;
  position: relative;
}

.toolbar button,
.color-btn,
.highlight-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  min-width: 28px;
  padding: 0 6px;
  font-size: 14px;
}

/* penna icon */
.highlight-icon {
  width: 16px;
  height: 16px;
  background: #000;
  mask: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M2 22l3-1 14-14-3-3L2 22z"/></svg>') no-repeat center;
  -webkit-mask: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M2 22l3-1 14-14-3-3L2 22z"/></svg>') no-repeat center;
  mask-size: contain;
  -webkit-mask-size: contain;
  margin-bottom: 2px;
}

/* small color bar under icon */
.highlight-color-bar {
  width: 14px;
  height: 3px;
  border-radius: 2px;
  margin-top: 2px;
}

.color-palette {
  position: absolute;
  display: flex;
  flex-wrap: wrap;
  width: 140px;
  padding: 6px;
  background: white;
  border: 1px solid #ccc;
  border-radius: 6px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  z-index: 999;
  gap: 6px;
}

.text-palette {
  top: 44px;
  left: 120px;
}

.highlight-palette {
  top: 44px;
  left: 40px;
}

.color-box {
  width: 22px;
  height: 22px;
  border-radius: 3px;
  cursor: pointer;
  border: 1px solid #aaa;
}

.color-box:hover {
  transform: scale(1.1);
}

.remove-color {
  width: 100%;
  margin-top: 6px;
  border: none;
  padding: 4px 0;
  background: #eee;
  cursor: pointer;
  border-radius: 4px;
  text-align: center;
}

:deep(.ProseMirror mark) {
  display: inline !important;
}
</style>
